import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton
} from '@mui/material';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';
import type { Worker as WorkerType, LeaveHistoryEntry, LeaveRequestStatus } from '../../types/worker';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Popover from '@mui/material/Popover';

interface LeaveTabProps {
  farmId: string;
}

type Worker = WorkerType & { leaveDays: number; leaveHistory: LeaveHistoryEntry[] };
type LeaveRecord = LeaveHistoryEntry & { request_date?: string; leave_type?: string; start_date?: string; end_date?: string; };

const LeaveTab: React.FC<LeaveTabProps> = ({ farmId }) => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    worker_id: '',
    start_date: '',
    end_date: '',
    leave_type: 'annual',
    reason: '',
    status: 'pending'
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<LeaveRecord & { worker_name: string } | null>(null);
  const [editForm, setEditForm] = useState<any>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
  const [popoverLeave, setPopoverLeave] = useState<LeaveRecord & { worker_name: string } | null>(null);

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch workers for this farm with their leave data
      const workersData = await fetchWorkers(farmId);
      setWorkers(workersData);
    } catch (err) {
      console.error('Error loading leave data:', err);
      setError('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLeave = async () => {
    try {
      setError(null);
      // Find the worker
      const worker = workers.find(w => w.id === formData.worker_id);
      if (!worker) throw new Error('Worker not found');
      // Create new leave record
      const newLeaveRecord: LeaveRecord = {
        id: Date.now().toString(),
        startDate: formData.start_date,
        endDate: formData.end_date,
        days: 0,
        isPaid: false,
        reason: formData.reason,
        status: formData.status as LeaveRequestStatus,
        request_date: new Date().toISOString(),
        leave_type: formData.leave_type,
        start_date: formData.start_date,
        end_date: formData.end_date,
      };
      // Update worker's leave history
      const updatedLeaveHistory = [...(worker.leaveHistory || []), newLeaveRecord].map(lr => ({
        ...lr,
        request_date: lr.request_date || new Date().toISOString(),
        leave_type: lr.leave_type || formData.leave_type
      }));
      const { error } = await supabase
        .from('workers')
        .update({
          leave_history: updatedLeaveHistory
        })
        .eq('id', formData.worker_id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setDialogOpen(false);
      setFormData({
        worker_id: '',
        start_date: '',
        end_date: '',
        leave_type: 'annual',
        reason: '',
        status: 'pending'
      });
      loadData();
    } catch (err) {
      console.error('Error adding leave record:', err);
      setError('Failed to add leave record');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getAllLeaveRecords = () => {
    const allRecords: (LeaveRecord & { worker_name: string })[] = [];
    workers.forEach(worker => {
      if (worker.leaveHistory) {
        worker.leaveHistory.forEach(record => {
          allRecords.push({
            ...record,
            worker_name: worker.name
          });
        });
      }
    });
    return allRecords.sort((a, b) => {
      const dateA = typeof a.request_date === 'string' && a.request_date ? a.request_date : '1970-01-01';
      const dateB = typeof b.request_date === 'string' && b.request_date ? b.request_date : '1970-01-01';
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });
  };

  // Helper to safely get a date string for new Date()
  const safeDate = (val1?: string, val2?: string): string => {
    if (typeof val1 === 'string' && val1.length > 0) return val1;
    if (typeof val2 === 'string' && val2.length > 0) return val2;
    return '1970-01-01';
  };

  // CRUD: Edit
  const handleEditClick = async (leave: LeaveRecord & { worker_name: string }) => {
    // Find the worker by name
    const worker = workers.find(w => w.name === leave.worker_name);
    if (!worker) return;
    // Fetch the latest worker data from the DB
    const { data, error } = await supabase.from('workers').select('leave_history').eq('id', worker.id).single();
    if (error || !data) {
      setEditForm({ ...leave });
      setSelectedLeave(leave);
      setEditDialogOpen(true);
      return;
    }
    // Find the latest leave record by ID
    const latestLeave = (data.leave_history || []).find((lr: any) => lr.id === leave.id);
    setEditForm({ ...(latestLeave || leave) });
    setSelectedLeave(leave);
    setEditDialogOpen(true);
  };
  const handleEditFormChange = (field: string, value: any) => {
    setEditForm((prev: any) => ({ ...prev, [field]: value }));
  };
  const handleEditLeaveSubmit = async () => {
    try {
      if (!selectedLeave) return;
      const worker = workers.find(w => w.name === selectedLeave.worker_name);
      if (!worker) throw new Error('Worker not found');
      const updatedLeaveHistory = (worker.leaveHistory || []).map(lr =>
        lr.id === editForm.id
          ? {
              ...editForm,
              request_date: editForm.request_date || lr.request_date || new Date().toISOString(),
              leave_type: editForm.leave_type || lr.leave_type || 'annual',
            }
          : {
              ...lr,
              request_date: lr.request_date || new Date().toISOString(),
              leave_type: lr.leave_type || 'annual',
            }
      );
      const { error } = await supabase
        .from('workers')
        .update({ leave_history: updatedLeaveHistory })
        .eq('id', worker.id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setEditDialogOpen(false);
      setSelectedLeave(null);
      setEditForm(null);
      loadData();
    } catch (err) {
      setError('Failed to update leave record');
    }
  };
  // CRUD: Delete
  const handleDeleteClick = (leave: LeaveRecord & { worker_name: string }) => {
    setSelectedLeave(leave);
    setDeleteDialogOpen(true);
  };
  const handleDeleteLeave = async () => {
    try {
      if (!selectedLeave) return;
      const worker = workers.find(w => w.name === selectedLeave.worker_name);
      if (!worker) throw new Error('Worker not found');
      const updatedLeaveHistory = (worker.leaveHistory || []).filter(lr => lr.id !== selectedLeave.id);
      const { error } = await supabase
        .from('workers')
        .update({ leave_history: updatedLeaveHistory })
        .eq('id', worker.id)
        .eq('farm_id', farmId);
      if (error) throw error;
      setDeleteDialogOpen(false);
      setSelectedLeave(null);
      loadData();
    } catch (err) {
      setError('Failed to delete leave record');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Leave Management</Typography>
        <Button variant="contained" onClick={() => setDialogOpen(true)}>
          Add Leave Request
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Leave Type</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Request Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getAllLeaveRecords().map((record, index) => (
              <TableRow
                key={`${record.id}-${index}`}
                hover
                style={{ cursor: 'pointer' }}
              >
                <TableCell>{record.worker_name}</TableCell>
                <TableCell>{record.leave_type}</TableCell>
                <TableCell>{new Date('' + safeDate(record.startDate, record.start_date)).toLocaleDateString()}</TableCell>
                <TableCell>{new Date('' + safeDate(record.endDate, record.end_date)).toLocaleDateString()}</TableCell>
                <TableCell
                  onClick={e => {
                    e.stopPropagation();
                    setPopoverAnchor(e.currentTarget);
                    setPopoverLeave(record);
                  }}
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: '#1976d2' }}
                >
                  {record.reason || '-'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={record.status} 
                    color={getStatusColor(record.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{record.request_date && !isNaN(new Date(record.request_date).getTime()) ? new Date(record.request_date).toLocaleDateString() : '-'}</TableCell>
                <TableCell>
                  <Tooltip title="Edit Leave">
                    <IconButton onClick={e => { e.stopPropagation(); handleEditClick(record); }} size="small"><EditIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Leave">
                    <IconButton onClick={e => { e.stopPropagation(); handleDeleteClick(record); }} size="small"><DeleteIcon /></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Leave Request</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={formData.worker_id}
              onChange={(e) => setFormData({ ...formData, worker_id: e.target.value })}
              margin="normal"
              required
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>
                  {worker.name} (Available: {worker.leaveDays} days)
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Leave Type"
              fullWidth
              value={formData.leave_type}
              onChange={(e) => setFormData({ ...formData, leave_type: e.target.value })}
              margin="normal"
              required
            >
              <MenuItem value="annual">Annual Leave</MenuItem>
              <MenuItem value="sick">Sick Leave</MenuItem>
              <MenuItem value="personal">Personal Leave</MenuItem>
              <MenuItem value="emergency">Emergency Leave</MenuItem>
              <MenuItem value="maternity">Maternity Leave</MenuItem>
              <MenuItem value="paternity">Paternity Leave</MenuItem>
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                margin="normal"
                required
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                margin="normal"
                required
                sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <TextField
              label="Reason"
              fullWidth
              multiline
              rows={3}
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              margin="normal"
            />
            <TextField
              select
              label="Status"
              fullWidth
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              margin="normal"
              required
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddLeave} variant="contained">
            Add Request
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => { setPopoverAnchor(null); setPopoverLeave(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 350, maxWidth: 500 }}>
          {popoverLeave && (
            <>
              <Typography variant="h6" gutterBottom>{popoverLeave.worker_name}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>{popoverLeave.reason}</Typography>
              <Typography variant="body2"><b>Leave Type:</b> {popoverLeave.leave_type || '-'}</Typography>
              <Typography variant="body2"><b>Status:</b> {popoverLeave.status}</Typography>
              <Typography variant="body2"><b>Start Date:</b> {new Date('' + safeDate(popoverLeave.startDate, popoverLeave.start_date)).toLocaleDateString()}</Typography>
              <Typography variant="body2"><b>End Date:</b> {new Date('' + safeDate(popoverLeave.endDate, popoverLeave.end_date)).toLocaleDateString()}</Typography>
              <Typography variant="body2"><b>Request Date:</b> {
                (popoverLeave.request_date || popoverLeave.request_date) && !isNaN(new Date(popoverLeave.request_date || popoverLeave.request_date).getTime())
                  ? new Date(popoverLeave.request_date || popoverLeave.request_date).toLocaleDateString()
                  : '-'
              }</Typography>
            </>
          )}
        </Box>
      </Popover>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Leave Request</DialogTitle>
        <DialogContent>
          {editForm && (
            <Box sx={{ pt: 1 }}>
              <TextField
                label="Reason"
                fullWidth
                multiline
                rows={3}
                value={editForm.reason}
                onChange={e => handleEditFormChange('reason', e.target.value)}
                margin="normal"
              />
              <TextField
                label="Start Date"
                type="date"
                value={editForm.startDate || editForm.start_date}
                onChange={e => handleEditFormChange('startDate', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                value={editForm.endDate || editForm.end_date}
                onChange={e => handleEditFormChange('endDate', e.target.value)}
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                select
                label="Status"
                fullWidth
                value={editForm.status}
                onChange={e => handleEditFormChange('status', e.target.value)}
                margin="normal"
                required
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditLeaveSubmit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Leave Request</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this leave request?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteLeave}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveTab;